import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetTransactions, useDeleteTransaction } from '../../api/transactions';
import { useAppContext } from '../../contexts/Storage';
import { transactionEntitiesService, SimilaritySearchResult } from '../../services/transactionEntitiesService';
import { TransactionItem } from '../../models/TransactionItem';
import { TransactionFetchFilter } from '../../models/TransactionFetchFilter';

interface UseTransactionsOptions {
  recordId?: number;
}

const createDefaultFilter = (recordId?: number): TransactionFetchFilter => {
  const filter = new TransactionFetchFilter();
  if (recordId) {
    filter.record_id = recordId;
  }
  return filter;
};

const useTransactions = (options: UseTransactionsOptions = {}) => {
  const { recordId: initialRecordId } = options;
  const navigate = useNavigate();
  const { user, token } = useAppContext();

  // Selected record state
  const [selectedRecordId, setSelectedRecordId] = useState<number | undefined>(initialRecordId);

  // Filter state - using TransactionFetchFilter class instance
  const [filters, setFilters] = useState<TransactionFetchFilter>(() => createDefaultFilter(initialRecordId));

  // Entity filter display values (for autocomplete inputs)
  const [entityFilterValues, setEntityFilterValues] = useState({
    action: '',
    object: '',
    indirectObject: '',
  });

  // Update filters when recordId changes
  useEffect(() => {
    if (initialRecordId !== undefined && initialRecordId !== filters.record_id) {
      setFilters((prev) => {
        const next = Object.create(Object.getPrototypeOf(prev));
        Object.assign(next, prev, { record_id: initialRecordId, page: 1 });
        return next as TransactionFetchFilter;
      });
      setSelectedRecordId(initialRecordId);
    }
  }, [initialRecordId]);

  // Selected transaction for detail view
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionItem | null>(null);

  // Delete modal state
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    transaction: TransactionItem | null;
  }>({
    isOpen: false,
    transaction: null,
  });

  // API hooks - only fetch when we have a record_id
  const hasRecordId = filters.record_id !== undefined;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetTransactions({ filters, enabled: hasRecordId });

  const {
    mutate: deleteTransaction,
    isPending: isDeleting,
  } = useDeleteTransaction();

  // Derived data
  const transactions = useMemo(() => data?.transactions ?? [], [data?.transactions]);
  const pagination = useMemo(() => data?.pagination, [data?.pagination]);

    // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.transaction_action_id !== undefined ||
      filters.transaction_object_id !== undefined ||
      filters.transaction_indirect_object_id !== undefined ||
      filters.created_by_user_id !== undefined ||
      !!filters.hashedID ||
      !!filters.start_date ||
      !!filters.end_date ||
      filters.amount_min !== undefined ||
      filters.amount_max !== undefined
    );
  }, [filters]);

  // Filter handlers - preserve prototype chain of TransactionFetchFilter
  const updateFilter = useCallback((updates: Partial<TransactionFetchFilter>) => {
    setFilters((prev) => {
      const next = Object.create(Object.getPrototypeOf(prev));
      Object.assign(next, prev, updates);
      return next as TransactionFetchFilter;
    });
  }, []);

  const handleSearchFilter = useCallback((search: string) => {
    updateFilter({ hashedID: search.trim() || undefined, page: 1 });
  }, [updateFilter]);

  const handleRecordFilter = useCallback((recordId: number | undefined) => {
    setSelectedRecordId(recordId);
    updateFilter({ record_id: recordId, page: 1 });
  }, [updateFilter]);

  const handleDateFilter = useCallback((startDate?: string, endDate?: string) => {
    updateFilter({ start_date: startDate, end_date: endDate, page: 1 });
  }, [updateFilter]);

  const handlePageChange = useCallback((page: number) => {
    updateFilter({ page });
  }, [updateFilter]);

  const handleLimitChange = useCallback((limit: number) => {
    updateFilter({ limit, page: 1 });
  }, [updateFilter]);

  const resetFilters = useCallback(() => {
    setFilters(createDefaultFilter(selectedRecordId));
    setEntityFilterValues({ action: '', object: '', indirectObject: '' });
  }, [selectedRecordId]);

  const handleFilterChange = useCallback(
    (key: keyof TransactionFetchFilter, value: unknown) => {
      updateFilter({ [key]: value, page: key !== 'page' ? 1 : (value as number) });
    },
    [updateFilter]
  );

  // Entity filter handlers
  const handleEntityFilterValueChange = useCallback(
    (field: 'action' | 'object' | 'indirectObject', value: string) => {
      setEntityFilterValues((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleEntityFilterSelect = useCallback(
    (field: 'action' | 'object' | 'indirectObject', item: { id: number; name: string }) => {
      setEntityFilterValues((prev) => ({ ...prev, [field]: item.name }));
      
      const filterKeyMap: Record<string, keyof TransactionFetchFilter> = {
        action: 'transaction_action_id',
        object: 'transaction_object_id',
        indirectObject: 'transaction_indirect_object_id',
      };
      
      updateFilter({ [filterKeyMap[field]]: item.id, page: 1 });
    },
    [updateFilter]
  );

  const clearEntityFilter = useCallback(
    (field: 'action' | 'object' | 'indirectObject') => {
      setEntityFilterValues((prev) => ({ ...prev, [field]: '' }));
      
      const filterKeyMap: Record<string, keyof TransactionFetchFilter> = {
        action: 'transaction_action_id',
        object: 'transaction_object_id',
        indirectObject: 'transaction_indirect_object_id',
      };
      
      updateFilter({ [filterKeyMap[field]]: undefined, page: 1 });
    },
    [updateFilter]
  );

    const clearAllFilters = useCallback(() => {
    setEntityFilterValues({ action: '', object: '', indirectObject: '' });
    updateFilter({
      transaction_action_id: undefined,
      transaction_object_id: undefined,
      transaction_indirect_object_id: undefined,
      created_by_user_id: undefined,
      hashedID: undefined,
      start_date: undefined,
      end_date: undefined,
      amount_min: undefined,
      amount_max: undefined,
      page: 1,
    });
  }, [updateFilter]);

  // Create fetchSuggestions function for entity autocomplete
  const createFetchSuggestions = useCallback(
    (category: 'action' | 'object' | 'indirect_object' | 'media') => {
      return async (query: string): Promise<{ id: number; name: string; description: string }[]> => {
        if (!user?.id || !token) {
          return [];
        }

        const response = await transactionEntitiesService.findByCosineSimilarity(
          token,
          user.id,
          query,
          category,
          10
        );

        if (!response.success || !response.data) {
          return [];
        }

        return response.data.map((item: SimilaritySearchResult) => ({
          id: item.entity.id,
          name: item.entity.transaction_entity_name,
          description: item.entity.transaction_entity_description || '',
        }));
      };
    },
    [user?.id, token]
  );

  // Transaction selection
  const handleSelectTransaction = useCallback((transaction: TransactionItem) => {
    setSelectedTransaction(transaction);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedTransaction(null);
  }, []);

  // Navigation
  const handleCreateTransaction = useCallback(() => {
    navigate('/records/transactions/new');
  }, [navigate]);

  const handleEditTransaction = useCallback((transaction: TransactionItem) => {
    if (transaction.record_id === undefined || transaction.id === undefined) return;
    navigate(`/records/transactions/edit/${transaction.record_id}/${transaction.id}`, {
      state: { transaction },
    });
  }, [navigate]);

  // Delete flow
  const openDeleteModal = useCallback((transaction: TransactionItem) => {
    setDeleteModalState({
      isOpen: true,
      transaction,
    });
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (!isDeleting) {
      setDeleteModalState({
        isOpen: false,
        transaction: null,
      });
    }
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(
    async (transactionId: number) => {
      deleteTransaction(
        { transactionId },
        {
          onSuccess: () => {
            closeDeleteModal();
            if (selectedTransaction?.id === transactionId) {
              setSelectedTransaction(null);
            }
          },
        }
      );
    },
    [deleteTransaction, closeDeleteModal, selectedTransaction]
  );

  return {
    // Data
    transactions,
    pagination,
    selectedTransaction,

    // Record selection
    selectedRecordId,
    hasRecordId,

    // Loading states
    isLoading,
    isDeleting,

    // Error state
    error,

    // Filter state and handlers
    filters,
    handleFilterChange,
    handleSearchFilter,
    handleRecordFilter,
    handleDateFilter,
    handlePageChange,
    handleLimitChange,
    resetFilters,

    // Entity filter state and handlers
    entityFilterValues,
    handleEntityFilterValueChange,
    handleEntityFilterSelect,
    clearEntityFilter,
    clearAllFilters,
    hasActiveFilters,
    createFetchSuggestions,

    // Transaction actions
    handleSelectTransaction,
    handleCloseDetail,
    handleCreateTransaction,
    handleEditTransaction,

    // Delete modal
    deleteModalState,
    openDeleteModal,
    closeDeleteModal,
    handleConfirmDelete,

    // Refetch
    refetch,
  };
};

export default useTransactions;
